/**
 * SectionExemplarsSheet — per-section Library exemplars.
 *
 * Priority #5: the tender-wide LibraryExemplarsPanel asks "what past
 * proposals look like THIS tender?". This component drills one level
 * deeper — "what past proposal language fits THIS section?" — by
 * sending the section's body text as the search query.
 *
 * Why a per-section view: a single RFP often spans tech / commercial /
 * legal. Tender-wide matching surfaces the closest overall analogue;
 * per-section matching surfaces the cleanest piece of prior art for
 * the clause you're drafting right now (cover-letter boilerplate for
 * the intro, your firm's BIS certification statement for the tech
 * section, the standard EMD undertaking for the financial section).
 *
 * Backend: GET /api/v2/library/search?q=&kind=&limit=. We cap `q` at
 * 600 chars — Postgres plainto_tsquery happily eats long inputs, but
 * the trigram extension we lean on for fallback ranks better on a
 * tight signal.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen, ExternalLink, Copy, Loader2, AlertTriangle, FileText, Mail,
} from "lucide-react";
import { toast } from "sonner";

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { v2 } from "@/lib/v2Api";


const QUERY_CHARS = 600;


function deriveQuery(section) {
  const title = (section?.title || "").trim();
  const body = (section?.content || section?.body || "").trim();
  // Keep the title as the prefix so it gets the lexeme weight, but let
  // the body provide topical anchors. Bare title alone is too short.
  const combined = (title + "\n" + body).slice(0, QUERY_CHARS);
  return combined.trim() || title || "tender";
}


function HitRow({ hit, kind }) {
  const Icon = kind === "cover_letter" ? Mail : FileText;
  const copy = () => {
    const text = (hit.snippet || hit.summary || "").replace(/<[^>]+>/g, "");
    if (!text) { toast.error("Nothing to copy"); return; }
    navigator.clipboard.writeText(text).then(
      () => toast.success("Snippet copied"),
      () => toast.error("Clipboard blocked"),
    );
  };
  return (
    <li className="border rounded-md p-3 bg-white space-y-2">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">
            {hit.title || hit.client_name || "(untitled)"}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {hit.industry && (
              <Badge variant="outline" className="text-[10px]">
                {hit.industry}
              </Badge>
            )}
            {hit.sector && (
              <Badge variant="outline" className="text-[10px]">
                {hit.sector}
              </Badge>
            )}
            {hit.year && (
              <Badge variant="outline" className="text-[10px]">
                {hit.year}
              </Badge>
            )}
            {hit.score != null && (
              <span className="text-[10px] text-slate-400 self-center">
                rank {Number(hit.score).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
      {hit.snippet && (
        <p
          className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed"
          // Backend uses <b>/<mark> in ts_headline output; we trust the
          // backend's sanitiser and render it as-is to keep the highlights.
          dangerouslySetInnerHTML={{ __html: hit.snippet }}
        />
      )}
      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="ghost" onClick={copy} className="h-7 text-xs gap-1">
          <Copy className="w-3 h-3" /> Copy
        </Button>
        {hit.deep_link && (
          <Button
            size="sm" variant="ghost"
            onClick={() => window.open(hit.deep_link, "_blank", "noreferrer")}
            className="h-7 text-xs gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Button>
        )}
      </div>
    </li>
  );
}


export default function SectionExemplarsSheet({ open, onOpenChange, section }) {
  const [proposals, setProposals] = useState([]);
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeKind, setActiveKind] = useState("proposal");

  const q = useMemo(() => deriveQuery(section || {}), [section]);

  const runSearch = useCallback(async () => {
    if (!open || !q) return;
    setLoading(true); setError(null);
    try {
      const [pRes, cRes] = await Promise.all([
        v2.get("/library/search", { params: { q, kind: "proposal", limit: 5 } }),
        v2.get("/library/search", { params: { q, kind: "cover_letter", limit: 5 } }),
      ]);
      setProposals(Array.isArray(pRes.data) ? pRes.data : []);
      setCovers(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Search failed");
      setProposals([]); setCovers([]);
    } finally {
      setLoading(false);
    }
  }, [open, q]);

  useEffect(() => { runSearch(); }, [runSearch]);

  const hits = activeKind === "proposal" ? proposals : covers;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-500" />
            Exemplars — {section?.title || "section"}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Top matches from past proposals + cover letters, keyed off
            this section's text (not the whole tender).
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-3 border-b flex gap-2">
          <Button
            size="sm" variant={activeKind === "proposal" ? "default" : "outline"}
            onClick={() => setActiveKind("proposal")}
            className="h-7 text-xs gap-1"
          >
            <FileText className="w-3 h-3" /> Proposals ({proposals.length})
          </Button>
          <Button
            size="sm" variant={activeKind === "cover_letter" ? "default" : "outline"}
            onClick={() => setActiveKind("cover_letter")}
            className="h-7 text-xs gap-1"
          >
            <Mail className="w-3 h-3" /> Cover letters ({covers.length})
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-700">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          {!loading && !error && hits.length === 0 && (
            <p className="text-xs italic text-slate-400">
              No exemplars in the Library matched this section. Seed the
              Library with prior {activeKind === "proposal" ? "proposals" : "cover letters"} to get traction.
            </p>
          )}
          {hits.length > 0 && (
            <ul className="space-y-2.5">
              {hits.map((h, i) => (
                <HitRow key={h.id || i} hit={h} kind={activeKind} />
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
